/**
 * Permission utility functions
 * Provides helper functions to check user permissions based on role and permission data
 */

/**
 * Check if user has a specific permission
 * @param {Object} user - User object with userRole and permissions
 * @param {string} permissionCode - Permission code to check (e.g., 'order.read')
 * @returns {boolean} - True if user has permission
 */
export const hasPermission = (user, permissionCode) => {
  if (!user || !user.userRole || !user.userRole.permissions) {
    return false;
  }

  // Super Admin or Admin roles typically have all permissions
  if (user.userRole.name === 'Super Admin' || user.userRole.name === 'Admin') {
    return true;
  }

  // Check if user has the specific permission
  return user.userRole.permissions.some(permission => 
    permission.code === permissionCode && permission.isActive
  );
};

/**
 * Check if user has any of the specified permissions
 * @param {Object} user - User object with userRole and permissions
 * @param {Array<string>} permissionCodes - Array of permission codes to check
 * @returns {boolean} - True if user has at least one of the permissions
 */
export const hasAnyPermission = (user, permissionCodes) => {
  if (!Array.isArray(permissionCodes)) {
    return false;
  }

  return permissionCodes.some(code => hasPermission(user, code));
};

/**
 * Check if user has all specified permissions
 * @param {Object} user - User object with userRole and permissions
 * @param {Array<string>} permissionCodes - Array of permission codes to check
 * @returns {boolean} - True if user has all permissions
 */
export const hasAllPermissions = (user, permissionCodes) => {
  if (!Array.isArray(permissionCodes)) {
    return false;
  }

  return permissionCodes.every(code => hasPermission(user, code));
};

/**
 * Check if user can access a resource with specific action
 * @param {Object} user - User object with userRole and permissions
 * @param {string} resource - Resource name (e.g., 'order', 'sku_item')
 * @param {string} action - Action name (e.g., 'read', 'create', 'update', 'delete', 'manage')
 * @returns {boolean} - True if user can perform action on resource
 */
export const canAccessResource = (user, resource, action) => {
  if (!user || !user.userRole || !user.userRole.permissions) {
    return false;
  }

  // Super Admin or Admin roles typically have all permissions
  if (user.userRole.name === 'Super Admin' || user.userRole.name === 'Admin') {
    return true;
  }

  // Check if user has the specific resource.action permission or resource.manage permission
  return user.userRole.permissions.some(permission => 
    permission.resource === resource && 
    (permission.action === action || permission.action === 'manage') &&
    permission.isActive
  );
};

/**
 * Get user's permissions for a specific resource
 * @param {Object} user - User object with userRole and permissions
 * @param {string} resource - Resource name
 * @returns {Array<string>} - Array of action names user can perform on the resource
 */
export const getResourcePermissions = (user, resource) => {
  if (!user || !user.userRole || !user.userRole.permissions) {
    return [];
  }

  // Super Admin or Admin roles typically have all permissions
  if (user.userRole.name === 'Super Admin' || user.userRole.name === 'Admin') {
    return ['read', 'create', 'update', 'delete', 'manage'];
  }

  return user.userRole.permissions
    .filter(permission => 
      permission.resource === resource && permission.isActive
    )
    .map(permission => permission.action);
};

// Order-specific permission constants
export const ORDER_PERMISSIONS = {
  VIEW: 'order.read',
  CREATE: 'order.create',
  UPDATE: 'order.update',
  DELETE: 'order.delete',
  MANAGE: 'order.manage',
  VIEW_FINANCIAL_DATA: 'order.view_financial_data',
  VIEW_KPI_DASHBOARD: 'order.view_kpi_dashboard',
  BULK_EDIT: 'order.bulk_edit',
  SEND_MOCKUP: 'order.send_mockup',
  SEND_FOLLOW_UP_MESSAGE: 'order.send_follow_up_message',
  FULFILL_TO_PRINTER: 'order.fulfill_to_printer',
  CONFIRM_SHIPMENT: 'order.confirm_shipment',
  SYNC_AMAZON: 'order.sync_amazon',
  UPLOAD_REPORT: 'order.upload_report',
  EXPORT_CSV: 'order.export_csv',
  VIEW_CUSTOMER_MESSAGES: 'order.view_customer_messages'
};

// Order-specific permission checker functions
export const orderPermissions = {
  canView: (user) => hasPermission(user, ORDER_PERMISSIONS.VIEW),
  canCreate: (user) => hasPermission(user, ORDER_PERMISSIONS.CREATE),
  canUpdate: (user) => hasPermission(user, ORDER_PERMISSIONS.UPDATE),
  canDelete: (user) => hasPermission(user, ORDER_PERMISSIONS.DELETE),
  canManage: (user) => hasPermission(user, ORDER_PERMISSIONS.MANAGE),
  canViewFinancialData: (user) => hasPermission(user, ORDER_PERMISSIONS.VIEW_FINANCIAL_DATA),
  canViewKpiDashboard: (user) => hasPermission(user, ORDER_PERMISSIONS.VIEW_KPI_DASHBOARD),
  canBulkEdit: (user) => hasPermission(user, ORDER_PERMISSIONS.BULK_EDIT),
  canSendMockup: (user) => hasPermission(user, ORDER_PERMISSIONS.SEND_MOCKUP),
  canSendFollowUpMessage: (user) => hasPermission(user, ORDER_PERMISSIONS.SEND_FOLLOW_UP_MESSAGE),
  canFulfillToPrinter: (user) => hasPermission(user, ORDER_PERMISSIONS.FULFILL_TO_PRINTER),
  canConfirmShipment: (user) => hasPermission(user, ORDER_PERMISSIONS.CONFIRM_SHIPMENT),
  canSyncAmazon: (user) => hasPermission(user, ORDER_PERMISSIONS.SYNC_AMAZON),
  canUploadReport: (user) => hasPermission(user, ORDER_PERMISSIONS.UPLOAD_REPORT),
  canExportCSV: (user) => hasPermission(user, ORDER_PERMISSIONS.EXPORT_CSV),
  canViewCustomerMessages: (user) => hasPermission(user, ORDER_PERMISSIONS.VIEW_CUSTOMER_MESSAGES)
};

// Category permission constants
export const CATEGORY_PERMISSIONS = {
  READ: 'category.read',
  CREATE: 'category.create',
  UPDATE: 'category.update',
  DELETE: 'category.delete',
  MANAGE: 'category.manage',
  MANAGE_PRODUCT_CATEGORIES: 'category.manage_product_categories',
  MANAGE_PRINTER_CATEGORIES: 'category.manage_printer_categories',
  MANAGE_AUTO_MESSAGES: 'category.manage_auto_messages',
  MANAGE_MAPPINGS: 'category.manage_mappings'
};

/**
 * Category permission helpers
 */
export const categoryPermissions = {
  canRead: (user) => hasPermission(user, CATEGORY_PERMISSIONS.READ),
  canCreate: (user) => hasPermission(user, CATEGORY_PERMISSIONS.CREATE),
  canUpdate: (user) => hasPermission(user, CATEGORY_PERMISSIONS.UPDATE),
  canDelete: (user) => hasPermission(user, CATEGORY_PERMISSIONS.DELETE),
  canManage: (user) => hasPermission(user, CATEGORY_PERMISSIONS.MANAGE),
  canManageProductCategories: (user) => hasPermission(user, CATEGORY_PERMISSIONS.MANAGE_PRODUCT_CATEGORIES),
  canManagePrinterCategories: (user) => hasPermission(user, CATEGORY_PERMISSIONS.MANAGE_PRINTER_CATEGORIES),
  canManageAutoMessages: (user) => hasPermission(user, CATEGORY_PERMISSIONS.MANAGE_AUTO_MESSAGES),
  canManageMappingSettings: (user) => hasPermission(user, CATEGORY_PERMISSIONS.MANAGE_MAPPINGS)
};

// Ads Tool permission constants - Quản lý Rules
export const ADS_PERMISSIONS = {
  READ: 'ads.read',
  CREATE: 'ads.create',
  UPDATE: 'ads.update',
  DELETE: 'ads.delete',
  MANAGE: 'ads.manage',
  DUPLICATE: 'ads.duplicate',
  TOGGLE_STATUS: 'ads.toggle_status'
};

/**
 * Ads Tool permission helpers - Quản lý Rules trong RuleList
 */
export const adsPermissions = {
  canRead: (user) => hasPermission(user, ADS_PERMISSIONS.READ),
  canCreate: (user) => hasPermission(user, ADS_PERMISSIONS.CREATE),
  canUpdate: (user) => hasPermission(user, ADS_PERMISSIONS.UPDATE),
  canDelete: (user) => hasPermission(user, ADS_PERMISSIONS.DELETE),
  canManage: (user) => hasPermission(user, ADS_PERMISSIONS.MANAGE),
  canDuplicate: (user) => hasPermission(user, ADS_PERMISSIONS.DUPLICATE),
  canToggleStatus: (user) => hasPermission(user, ADS_PERMISSIONS.TOGGLE_STATUS)
};

// Workflow permission constants - Flow Builder (module riêng)
export const WORKFLOW_PERMISSIONS = {
  READ: 'workflow.read',
  CREATE: 'workflow.create',
  UPDATE: 'workflow.update',
  DELETE: 'workflow.delete',
  MANAGE: 'workflow.manage',
  EXECUTE: 'workflow.execute'
};

/**
 * Workflow permission helpers - Flow Builder
 */
export const workflowPermissions = {
  canRead: (user) => hasPermission(user, WORKFLOW_PERMISSIONS.READ),
  canCreate: (user) => hasPermission(user, WORKFLOW_PERMISSIONS.CREATE),
  canUpdate: (user) => hasPermission(user, WORKFLOW_PERMISSIONS.UPDATE),
  canDelete: (user) => hasPermission(user, WORKFLOW_PERMISSIONS.DELETE),
  canManage: (user) => hasPermission(user, WORKFLOW_PERMISSIONS.MANAGE),
  canExecute: (user) => hasPermission(user, WORKFLOW_PERMISSIONS.EXECUTE)
};

/**
 * Higher-order component to check permissions before rendering component
 * @param {React.Component} Component - Component to wrap
 * @param {string|Array<string>} requiredPermissions - Permission(s) required to access component
 * @param {React.Component} FallbackComponent - Component to render if no permission (optional)
 * @returns {React.Component} - Wrapped component with permission check
 */
export const withPermission = (Component, requiredPermissions, FallbackComponent = null) => {
  return (props) => {
    const { user } = props;
    
    let hasRequiredPermission = false;
    
    if (Array.isArray(requiredPermissions)) {
      hasRequiredPermission = hasAnyPermission(user, requiredPermissions);
    } else {
      hasRequiredPermission = hasPermission(user, requiredPermissions);
    }
    
    if (!hasRequiredPermission) {
      return FallbackComponent || null;
    }
    
    return <Component {...props} />;
  };
};

/**
 * React hook to check permissions
 * @param {Object} user - User object
 * @param {string|Array<string>} permissions - Permission(s) to check
 * @returns {boolean} - True if user has required permission(s)
 */
export const usePermission = (user, permissions) => {
  if (Array.isArray(permissions)) {
    return hasAnyPermission(user, permissions);
  }
  return hasPermission(user, permissions);
};
